/* eslint-disable */
import { toast } from 'sonner';
import { useState } from 'react';
import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { Ban, Check, GripIcon, Pencil, Trash2 } from 'lucide-react';
import {
  ProductOffer,
  ProductOfferOrderWithRelations,
} from '@durma-soft/gros-sdk/dist/types/product-offers';
import {
  useAuthUserRestaurant,
  useConfirmProductOfferOrderMutation,
  useDeleteProductOfferMutation,
  useUnconfirmProductOfferOrderMutation,
} from '@durma-soft/gros-sdk';

import { Link } from '@/components/shared/link';
import { Button } from '@/components/shared/shadcn-ui/button';
import { ConfirmationModal } from '@/components/shared/confirmation-modal';
import { ProductOfferImage } from '@/components/restaurant/product-offers/product-offer-image';
import { DenyProductOfferOrderModal } from '@/components/restaurant/product-offers/deny-product-offer-order-modal';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/shared/shadcn-ui/tooltip';

import { TType } from '@/types/general';
import { formatPrice, numericSort } from '@/utils/product-offers';

const RowDragHandleCell = ({ rowId }: { rowId: UniqueIdentifier }) => {
  const { attributes, listeners } = useSortable({ id: `${rowId}` });

  return (
    <button {...attributes} {...listeners}>
      <GripIcon />
    </button>
  );
};

export const manageOfferColumns = (t: TType): ColumnDef<ProductOffer>[] => [
  {
    id: 'drag-handle',
    header: '',
    cell: ({ row }) => <RowDragHandleCell rowId={row.id} />,
    size: 60,
  },
  {
    accessorKey: 'image',
    enableSorting: false,
    header: t('common.image'),
    cell: (cellData) => {
      const { image, name } = cellData.row.original;
      return <ProductOfferImage image={image} name={name} />;
    },
  },
  {
    accessorKey: 'name',
    header: t('common.name'),
  },
  {
    id: 'supplier',
    header: t('product-offers.supplier'),
    accessorFn: (row) => row.supplier?.name ?? '',
    cell: (cellData) => cellData.row.original.supplier?.name ?? '—',
  },
  {
    accessorKey: 'price_per_unit',
    header: t('product-offers.price-per-unit'),
    sortingFn: numericSort,
    cell: (cellData) => formatPrice(cellData.row.original.price_per_unit),
  },
  {
    accessorKey: 'unit',
    header: t('product-offers.unit'),
  },
  {
    accessorKey: 'currency',
    header: t('product-offers.currency'),
  },
  {
    accessorKey: 'edit',
    enableSorting: false,
    header: '',
    cell: (cellData) => {
      const restaurant = useAuthUserRestaurant();

      const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false);

      const { isPending, mutateAsync } = useDeleteProductOfferMutation();

      const deleteProductOffer = async () => {
        try {
          await mutateAsync({
            restaurant_id: restaurant.id,
            product_offer_id: cellData.row.original.id,
          });
          toast.success(t('product-offers.delete-success-msg'));
        } catch (error) {
          toast.error(t('product-offers.delete-error-msg'));
        } finally {
          setIsConfirmationModalOpen(false);
        }
      };

      return (
        <TooltipProvider delayDuration={100}>
          <div className="flex items-center justify-center gap-5">
            {/* Edit */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  replace
                  to={`/restaurant/platform/product-offers/edit/${cellData.row.original.id}`}
                  className="flex items-center justify-center text-gray-700"
                >
                  <Pencil className="w-5 h-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                {t('product-offers.edit-details')}
              </TooltipContent>
            </Tooltip>
            {/* Delete */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-center text-gray-700 p-0"
                  onClick={() => setIsConfirmationModalOpen(true)}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('common.delete')}</TooltipContent>
            </Tooltip>
          </div>
          <ConfirmationModal
            isLoading={isPending}
            isModalOpen={isConfirmationModalOpen}
            confirmationText={t('common.delete')}
            title={t('product-offers.delete')}
            description={t('product-offers.delete-description', {
              productOffer: cellData.row.original.name.toLowerCase(),
            })}
            onConfirm={deleteProductOffer}
            onCancel={() => setIsConfirmationModalOpen(false)}
            closeModal={() => setIsConfirmationModalOpen(false)}
          />
        </TooltipProvider>
      );
    },
  },
];

export const allOrderColumns = (
  t: TType,
): ColumnDef<ProductOfferOrderWithRelations>[] => [
  {
    accessorKey: 'created_at',
    header: t('common.date'),
    cell: (cellData) =>
      format(new Date(cellData.row.original.created_at), 'dd.MM.yyyy HH:mm'),
  },
  {
    id: 'restaurant',
    header: t('product-offers.restaurant'),
    cell: (cellData) => cellData.row.original.restaurant.name,
  },
  {
    id: 'ordered-by',
    header: t('product-offers.ordered-by'),
    cell: (cellData) => {
      const { user } = cellData.row.original;
      return `${user.first_name} ${user.last_name}`;
    },
  },
  {
    id: 'product',
    header: t('common.name'),
    cell: (cellData) => cellData.row.original.product_offer.name,
  },
  {
    id: 'supplier',
    header: t('product-offers.supplier'),
    accessorFn: (row) => row.product_offer.supplier?.name ?? '',
    cell: (cellData) =>
      cellData.row.original.product_offer.supplier?.name ?? '—',
  },
  {
    accessorKey: 'quantity',
    header: t('common.quantity'),
    sortingFn: numericSort,
    cell: (cellData) =>
      `${Number(cellData.row.original.quantity)} ${cellData.row.original.unit}`,
  },
  {
    accessorKey: 'price_per_unit',
    header: t('product-offers.price-per-unit'),
    sortingFn: numericSort,
    cell: (cellData) =>
      `${formatPrice(cellData.row.original.price_per_unit)} ${cellData.row.original.currency}`,
  },
  {
    id: 'total',
    enableSorting: false,
    header: t('product-offers.total'),
    cell: (cellData) => {
      const { price_per_unit, quantity, currency } = cellData.row.original;
      return `${(Number(price_per_unit) * Number(quantity)).toFixed(2)} ${currency}`;
    },
  },
  {
    id: 'status',
    enableSorting: false,
    header: t('product-offers.status'),
    cell: (cellData) => {
      const restaurant = useAuthUserRestaurant();
      const confirmOrder = useConfirmProductOfferOrderMutation();
      const unconfirmOrder = useUnconfirmProductOfferOrderMutation();
      const [isDenyModalOpen, setIsDenyModalOpen] = useState(false);

      const order = cellData.row.original;
      const { confirmed_at, denied_at, deleted_at, id } = order;

      if (deleted_at) {
        return (
          <span className="text-muted-foreground">
            {t('product-offers.cancelled')}
          </span>
        );
      }

      if (denied_at) {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm text-destructive"
              >
                <Ban className="w-4 h-4" />
                {t('product-offers.denied')}
              </button>
            </PopoverTrigger>
            <PopoverContent className="max-w-xs text-sm">
              {order.denial_reason}
            </PopoverContent>
          </Popover>
        );
      }

      if (confirmed_at) {
        const handleUnconfirm = async () => {
          if (unconfirmOrder.isPending) return;
          try {
            await unconfirmOrder.mutateAsync({
              restaurant_id: restaurant.id,
              product_offer_order_id: id,
            });
            toast.success(t('product-offers.unconfirm-success-msg'));
          } catch {
            toast.error(t('product-offers.unconfirm-error-msg'));
          }
        };

        return (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Check className="w-4 h-4" />
              {t('product-offers.confirmed')}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={unconfirmOrder.isPending}
              onClick={handleUnconfirm}
            >
              {t('product-offers.unconfirm')}
            </Button>
          </div>
        );
      }

      const handleConfirm = async () => {
        if (confirmOrder.isPending) return;
        try {
          await confirmOrder.mutateAsync({
            restaurant_id: restaurant.id,
            product_offer_order_id: id,
          });
          toast.success(t('product-offers.confirm-success-msg'));
        } catch {
          toast.error(t('product-offers.confirm-error-msg'));
        }
      };

      return (
        <>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={confirmOrder.isPending}
              onClick={handleConfirm}
            >
              {t('product-offers.confirm')}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setIsDenyModalOpen(true)}
            >
              {t('product-offers.deny')}
            </Button>
          </div>
          {isDenyModalOpen && (
            <DenyProductOfferOrderModal
              order={order}
              onClose={() => setIsDenyModalOpen(false)}
            />
          )}
        </>
      );
    },
  },
];
